// src/components/assets/AssetDetailModal.tsx
import React from 'react';
import { X, Edit, Trash2, Package, CheckCircle, Clock, AlertTriangle } from 'lucide-react';
import { TrackedAsset, FUNDING_SOURCE_LABELS, DISPOSAL_METHOD_LABELS } from '../../types/asset';

interface AssetDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  asset: TrackedAsset;
  onEdit: () => void;
  onDispose: () => void;
}

const AssetDetailModal: React.FC<AssetDetailModalProps> = ({ isOpen, onClose, asset, onEdit, onDispose }) => {
  if (!isOpen) return null;

  const formatCurrency = (value?: number) => value != null ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value) : '—';
  const formatDate = (d?: string) => d ? new Date(d).toLocaleDateString() : '—';
  const formatDateTime = (d?: string) => d ? new Date(d).toLocaleString() : '—';

  const VerificationBadge = () => {
    const badges: Record<string, { bg: string; text: string; icon: React.ElementType; label: string }> = {
      verified: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle, label: 'Verified' },
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock, label: 'Due Soon' },
      overdue: { bg: 'bg-red-100', text: 'text-red-800', icon: AlertTriangle, label: 'Overdue' },
      new: { bg: 'bg-blue-100', text: 'text-blue-800', icon: Package, label: 'New' }
    };
    const b = badges[asset.verificationStatus] || badges.new;
    const Icon = b.icon;
    return <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${b.bg} ${b.text}`}><Icon className="w-4 h-4 mr-1" />{b.label}</span>;
  };

  const DetailRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div className="py-2 border-b border-gray-100 last:border-0">
      <dt className="text-sm text-gray-500">{label}</dt>
      <dd className="text-sm font-medium text-gray-900 mt-0.5">{value || '—'}</dd>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-gray-50">
          <div>
            <p className="text-sm font-mono text-blue-600">{asset.assetTag}</p>
            <h2 className="text-xl font-bold text-gray-800">{asset.name}</h2>
            {asset.status === 'disposed' && <span className="inline-block mt-1 px-2 py-0.5 text-xs rounded-full bg-gray-200 text-gray-600">Disposed</span>}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
        </div>

        <div className="p-6 space-y-6">
          <div className="flex items-center justify-between">
            <VerificationBadge />
            {asset.lastVerifiedAt && <span className="text-sm text-gray-500">Last verified: {formatDate(asset.lastVerifiedAt)}</span>}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-1">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-2">Details</h3>
              <dl>
                <DetailRow label="Category" value={asset.categoryName} />
                <DetailRow label="Location" value={asset.location} />
                <DetailRow label="Serial Number" value={asset.serialNumber} />
                <DetailRow label="Manufacturer" value={asset.manufacturer} />
                <DetailRow label="Model" value={asset.model} />
              </dl>
            </div>
            <div className="space-y-1">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-2">Financial</h3>
              <dl>
                <DetailRow label="Purchase Date" value={formatDate(asset.purchaseDate)} />
                <DetailRow label="Purchase Value" value={formatCurrency(asset.purchaseValue)} />
                <DetailRow label="Current Value" value={formatCurrency(asset.currentValue)} />
                <DetailRow label="Funding Source" value={asset.fundingSource ? FUNDING_SOURCE_LABELS[asset.fundingSource] : undefined} />
                {asset.grantId && <DetailRow label="Grant ID" value={asset.grantId} />}
              </dl>
            </div>
          </div>

          {asset.description && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-2">Description</h3>
              <p className="text-sm text-gray-600">{asset.description}</p>
            </div>
          )}

          {asset.notes && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-2">Notes</h3>
              <p className="text-sm text-gray-600">{asset.notes}</p>
            </div>
          )}

          {asset.status === 'disposed' && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-2">Disposal Information</h3>
              <dl className="grid grid-cols-2 gap-2">
                <DetailRow label="Disposed On" value={formatDate(asset.disposedAt)} />
                <DetailRow label="Method" value={asset.disposalMethod ? DISPOSAL_METHOD_LABELS[asset.disposalMethod] : undefined} />
                <DetailRow label="Disposed To" value={asset.disposedTo} />
                <DetailRow label="Amount" value={formatCurrency(asset.disposalAmount)} />
                <DetailRow label="Authorized By" value={asset.disposalAuthorizedBy} />
                <DetailRow label="Reference" value={asset.disposalReference} />
              </dl>
              {asset.disposalNotes && <p className="text-sm text-gray-600 mt-2">{asset.disposalNotes}</p>}
            </div>
          )}

          <div className="text-xs text-gray-400 pt-4 border-t">
            <p>Created: {formatDateTime(asset.createdAt)} by {asset.createdBy}</p>
            <p>Updated: {formatDateTime(asset.updatedAt)} by {asset.updatedBy}</p>
          </div>
        </div>

        {asset.status === 'active' && (
          <div className="flex justify-between p-6 border-t border-gray-200 bg-gray-50">
            <button onClick={onDispose} className="px-4 py-2 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg flex items-center">
              <Trash2 className="w-4 h-4 mr-2" />Dispose Asset
            </button>
            <button onClick={onEdit} className="px-4 py-2 text-white rounded-lg flex items-center" style={{ backgroundColor: '#082759' }}>
              <Edit className="w-4 h-4 mr-2" />Edit Asset
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssetDetailModal;